/**
 * overlayPlacement.ts —— 窄屏浮层（加注抽屉 / 赢率展开层）的放置约束求解
 *
 * 从 `GameScreen.tsx` 的实测 effect 中抽出的**纯函数**，输入全部是实测几何量，输出两组
 * CSS 变量所需的「目标底边绝对 Y」与「限高」。抽出来的目的有两个：
 *   1. 这段几何约束已经连续三轮出问题（QA-BUG-012 / QA-BUG-018 / REG3-NEW-001），
 *      必须能用毫秒级单测锁定，而不是每次都靠跑 23 格浏览器量测才发现；
 *   2. 让「约束优先级」在代码里显式写出来，不再靠散落的魔法数字表达。
 *
 * ── 约束优先级（从不可让到可让）────────────────────────────────────────────
 *   P1 受保护读数区：公共牌带 + 底池胶囊（`.felt__center`）。浮层**顶边**不得越过其下沿。
 *      理由：底池金额是下注决策的关键读数，且牌桌信息**不可滚动**、被遮就是读不到。
 *   P1 我的席位卡 / 赢率折叠把手 / 行动按钮行。浮层**底边**不得越过三者上沿的更小者。
 *      理由：底牌与筹码同样是决策读数；把手与按钮被遮等于功能不可达。
 *   P2 浮层高度：可让。浮层自身 `overflow-y:auto`，放不下就内部滚动。
 *   P3 视觉安全间距（gap）：最后才让。它只影响观感，不影响任何信息可读性。
 * ─────────────────────────────────────────────────────────────────────────
 */

export interface OverlayBounds {
  /** 浮层底边的下界线：页脚上沿（赢率层）/ 按钮行上沿与赢率把手上沿的更小者（抽屉） */
  limitTop: number;
  /** 「我」的席位卡上沿（含姓名 / 筹码 / 本街投入行 / 底牌，整块受保护） */
  meTop: number;
  /** 「我」的席位卡下沿 */
  meBottom: number;
  /** 受保护读数区下沿（`.felt__center` = 公共牌带 + 底池胶囊） */
  protectedBottom: number;
  /** 视觉安全间距，默认 8px */
  gap?: number;
  /** 「放在席位卡下方」这条放置带的可用门槛，低于它才抬到席位卡上方 */
  minOpenH?: number;
  /** 浮层高度低于此值时开始让出视觉间距（见 P3） */
  elasticAt?: number;
  /**
   * 浮层内容的自然高度需求（可选，默认 0 = 与改前逐像素一致）。
   *
   * 为什么需要它：P3 的定义是「视觉间距只影响观感」，那么当**让出间距就能避免内部滚动**时，
   * 继续留着 8px 观感、让 CTA 只露一半（BLK-F1 在 568 高的实测：抽屉 h84 / 内容 98、
   * CTA 可见比 0.75 且必须内滚），显然是把 P3 排在了 P2 前面 —— 与文件头声明的优先级相反。
   * 传入内容需求后，求解器会在**不越任何硬界**的前提下先把间距让到刚好够用（最多让到 0），
   * 让不动才回落到「浮层变矮 + 内部滚动」。不传时行为完全不变，宽屏与既有格不受影响。
   */
  contentMin?: number;
}

export interface OverlayPlacement {
  /** 目标底边绝对 Y（样式层再按实测包含块换算成 lift） */
  bottomY: number;
  /** 限高（写入 `--*-open-max`） */
  max: number;
  /** 落在哪条放置带上 */
  band: 'below' | 'above';
  /** 顶部 / 底部实际让出的视觉间距 */
  padTop: number;
  padBottom: number;
  /** 是否已经把视觉间距让出去换高度（诊断用） */
  gapsSpent: boolean;
  /**
   * 该放置带在**零视觉间距**时的可用高度（BLK-F1 的模式判据输入）。
   *
   * 注意它是**按选中的放置带**算的，不是一个全局量：
   *   - `below`（席位卡下方，宽屏 oval）→ 下界线 − 席位卡下沿
   *   - `above`（抬到席位卡上方）      → min(下界线, 席位卡上沿) − 受保护读数区下沿
   * 早先只按 `above` 的公式报，宽屏走 `below` 时会报出一个与实际可用空间无关的小值，
   * 把「浮层还装不装得下」判反（新增的 round8 单测就是在这里红的）。
   */
  hardBand: number;
}

/**
 * 触控热区一行 44px（无障碍下限）+ 抽屉上下内边距 2×12px = 68px，取 72px 与第 6 轮口径连续。
 * 注意语义变化：它**不再是**「可以压过受保护读数区的高度地板」，而只是
 * 「低于此高度就开始让出视觉间距」的弹性阈值。
 */
export const OVERLAY_ELASTIC_AT = 72;

/**
 * ── BLK-F1：加注抽屉的「承载模式」分级求解（方案 B）──────────────────────────
 *
 * 现场：设计终验收在 360 / 390 × 640 实测到抽屉两条硬界之间只有 **73px**，而抽屉内容
 * 自然高度 **240px**。滑杆被裁 95px、CTA 被裁 157px、两者可见比 0，指针路径完全无法提交
 * 加注，只有键盘 Tab+Enter 走得通。
 *
 * 这个区域连续三轮翻车（第 6 轮压底牌 / 第 7 轮切底池 / 第 8 轮切短屏），共同点都是
 * **把「装不下」当成「摆不对」**，于是一轮一轮去挤压参数（压浮层高度、压内边距、压行距）。
 * 挤压式修法的结构性问题是：可用带宽是**动态**的（人数档 / 是否贴盲注 / 是否有本街投入
 * 都会改变席位卡高度），任何"刚好卡住"的参数都只在标定那一格成立。
 *
 * 本轮改成从**空间预算**一次性解决，方法论沿用第 7 轮 `placeOverlay` 的分级求解
 * （见 docs/fix-round7.md §1.3：把约束按优先级显式排序，不可让的绝不让）：
 *
 *   ① 交互形态本身变成**可让的一档**，而不是不变量。抽屉不再只有一种排版，而是三档：
 *
 *      | 模式      | 承载内容                                   | 纵向需求 |
 *      |-----------|--------------------------------------------|----------|
 *      | `full`    | 快捷档一行 + 滑杆行 + 数字输入 + 确认 CTA  | 240      |
 *      | `compact` | 快捷档一行 + （滑杆 / 输入 / CTA 同一行）  | 114      |
 *      | `short`   | **无滑杆**：3 个快捷档位 + 确认 CTA 单行   | 66       |
 *
 *   ② 选档依据是**实测可用带宽**（`placeOverlay(...).hardBand`，与 contentMin 无关，
 *      因此不存在测量回环），取「装得下的最富模式」；
 *   ③ 再叠加一条**声明式下限**：视口高 ≤640 一律用 `short`。这是设计终验收给的放行口径
 *      （方案 B：短屏去掉滑杆、只留快捷档 + 确认），不让"带宽恰好够"把滑杆又放回短屏。
 *
 * 预算账（360 宽 × 2 人 = 最窄格，实测见 `docs/fix-round8/evidence/s0-shortgeom-*.json`）：
 *
 *   | 视口高 | 硬带宽 | 选中模式  | 需求 | 余量 |
 *   |--------|--------|-----------|------|------|
 *   | 844    | 277    | full      | 240  | +37  |
 *   | 780    | 213    | compact   | 114  | +99  |
 *   | 720    | 153    | compact   | 114  | +39  |
 *   | 680    | 150    | compact   | 114  | +36  |
 *   | 640    | 172    | short     | 66   | +106 |
 *   | 600    | 132    | short     | 66   | +66  |
 *   | 568    | 100    | short     | 66   | +34  |
 *
 * 关键差别：余量从上一版的 +2px（568 格）变成 ≥+34px，不再是"卡住"而是"装得下且有富余"。
 */
export const SIZER_NEED = {
  /**
   * 快捷档一行 44 + 行距 4 + 滑杆行 44 + 行距 4 + 数字输入与 CTA 行 48
   * + 抽屉上下内边距 2×8 + 上下描边 2 + 说明行余量 70 ≈ 240（narrow 实测自然高度）。
   */
  full: 240,
  /**
   * 快捷档一行 44 + 行距 4 + （滑杆 / 输入 / CTA 同一行）48
   * + 抽屉上下内边距 2×8 + 上下描边 2 = 114。三件控件热区 44 / 44 / 48 一个不减。
   */
  compact: 44 + 4 + 48 + 2 * 8 + 2,
  /**
   * 方案 B 的单行承载：3 个快捷档位（44 热区）与确认 CTA（48）同一行，**没有滑杆**
   * + 抽屉上下内边距 2×8 + 上下描边 2 = 66。
   * 纵向只占一行，是"指针能完整提交加注"所需的绝对最小形态。
   */
  short: 48 + 2 * 8 + 2,
} as const;

export type SizerMode = keyof typeof SIZER_NEED;

/** 从富到简的降级顺序；选档时取第一个装得下的。 */
export const SIZER_LADDER: readonly SizerMode[] = ['full', 'compact', 'short'];

/**
 * 声明式短屏阈值：视口高 ≤640 一律走方案 B（无滑杆）。
 * 与设计终验收的失效阈值同一个数（该报告实测 ≥680 指针路径可用、=640 失效）。
 */
export const SIZER_SHORT_MAX_H = 640;

/** 某一档承载模式的纵向需求（喂给 `placeOverlay` 的 `contentMin`）。 */
export function sizerContentMin(mode: SizerMode): number {
  return SIZER_NEED[mode];
}

/**
 * 选择抽屉承载模式：`min(按视口高的声明下限, 按实测带宽装得下的最富档)`。
 *
 * `hardBand` 必须来自**不传 contentMin** 的那次求解（它与 contentMin 无关），
 * 因此「模式 → 需求 → 限高」是单向的，不会出现"改了模式又改变带宽"的回环。
 */
export function pickSizerMode(input: { hardBand: number; viewportH: number; mobileFloor?: boolean }): SizerMode {
  const byHeight: SizerMode = input.viewportH <= SIZER_SHORT_MAX_H ? 'short' : 'full';
  const byBand: SizerMode = SIZER_LADDER.find((m) => input.hardBand >= SIZER_NEED[m]) ?? 'short';
  /* 谁更保守取谁（阶梯里位置更后的那个）——两者都是「必须更简」的上界。 */
  let idx = Math.max(SIZER_LADDER.indexOf(byHeight), SIZER_LADDER.indexOf(byBand));
  /*
   * R14-1：移动端（stack 竖/横屏两目标 430×932 / 932×430）承载**下限 = compact**。
   *
   * 方案 B 的 `short`（无滑杆）在移动端被判为不可接受：加注面板必须恒含
   * 「滑杆 + 数字输入 + 快捷档 + 确认」四件（ActionBar 已改为所有档都渲染全件），
   * 短高度改由浮层内部滚动兜底，而不是砍掉滑杆/输入。因此移动端把降级**封顶在 compact**
   * （不比 compact 更简）：`SIZER_SHORT_MAX_H≤640` 的声明式下限只对非移动端（桌面 md/lg）生效。
   */
  if (input.mobileFloor) idx = Math.min(idx, SIZER_LADDER.indexOf('compact'));
  return SIZER_LADDER[idx]!;
}

export function placeOverlay(b: OverlayBounds): OverlayPlacement {
  const gap = b.gap ?? 8;
  const minOpenH = b.minOpenH ?? 140;
  const elasticAt = b.elasticAt ?? OVERLAY_ELASTIC_AT;

  /* 放置带 A：我的席位卡下方的空白带（宽屏 oval 布局下真实存在） */
  const below = Math.round(b.limitTop - gap - (b.meBottom + gap));
  /* 「抬到席位卡上方」这条带的零间距可用高度；`below` 带另算（见 OverlayPlacement.hardBand）。 */
  const bandAbove = Math.floor(Math.min(b.limitTop, b.meTop) - b.protectedBottom);
  if (below >= minOpenH) {
    return {
      bottomY: Math.round(b.limitTop - gap),
      max: below,
      band: 'below',
      padTop: gap,
      padBottom: gap,
      gapsSpent: false,
      hardBand: Math.floor(b.limitTop - b.meBottom),
    };
  }

  /*
   * 放置带 B：抬到我的席位卡上方，上界是受保护读数区下沿。
   *
   * REG3-NEW-001 的根因就在这一段：第 6 轮写的是
   *     max = Math.max(72, bottomY - (protectedBottom + gap))
   * 即用一个**静态高度地板**去覆盖「顶边不得越过读数区」这条硬约束——地板一旦大于
   * 实际可用带，顶边就会顶进底池胶囊（min360×2 人实测切掉底池金额 616px²、可见比 0.875）。
   * 而可用带是**动态**的：我的席位卡在贴盲注 / 有本街投入时会多出一行 `.seat__bet`，
   * 实测长高 24px（98 → 122），可用带随之从 97px 缩到 73px。所以任何静态地板值都只是
   * 「在标定时用的那个人数档上恰好成立」，换个人数档或换个手牌阶段就会再犯（第 6 轮把
   * 地板从 96 调到 72 就是这样：6 人桌的 81px 带过了，2 人桌的 73px 带没过）。
   *
   * 这里改为按 P1 > P2 > P3 的优先级求解，不再有可以压过硬约束的地板：
   *   1) 先按「两侧各留 gap」算：带宽够用（≥ elasticAt）时结果与第 6 轮**逐像素一致**，
   *      不去动那些已经通过的格；
   *   2) 带宽不够时，先把 P3（视觉间距）让出去换高度，最多让到 0；
   *   3) 让完还不够，就接受更矮的浮层（P2 可让，浮层自身 overflow-y:auto 内部滚动），
   *      但顶边/底边**永远**留在硬界之内。
   */
  const hardBottom = Math.min(b.limitTop, b.meTop);
  const hardTop = b.protectedBottom;
  const preferredBottom = Math.floor(hardBottom - gap);
  const preferredMax = Math.floor(preferredBottom - (hardTop + gap));
  const contentMin = b.contentMin ?? 0;
  if (preferredMax >= elasticAt) {
    /* 带宽够留满间距、且内容也装得下 → 与改前逐像素一致 */
    if (preferredMax >= contentMin) {
      return { bottomY: preferredBottom, max: preferredMax, band: 'above', padTop: gap, padBottom: gap, gapsSpent: false, hardBand: bandAbove };
    }
    /*
     * 带宽够留满间距，但内容装不下 → 先让 P3（观感），只让到「刚好够内容」为止：
     * 需求超过整条带时就把间距让到 0（此时顶/底边正好贴在硬界上，不越界），
     * 剩下的差额仍由 P2 承担（浮层内部滚动）。
     */
    const need = Math.min(contentMin, bandAbove);
    const padFit = Math.max(0, Math.min(gap, Math.floor((bandAbove - need) / 2)));
    return {
      bottomY: Math.floor(hardBottom - padFit),
      max: Math.max(0, bandAbove - 2 * padFit),
      band: 'above',
      padTop: padFit,
      padBottom: padFit,
      gapsSpent: padFit < gap,
      hardBand: bandAbove,
    };
  }
  /* 零视觉间距时的可用高度。floor 保证换算出的整数边界不会越过分数像素硬界。 */
  const band = bandAbove;
  const pad = Math.max(0, Math.min(gap, Math.floor((band - elasticAt) / 2)));
  return {
    bottomY: Math.floor(hardBottom - pad),
    max: Math.max(0, band - 2 * pad),
    band: 'above',
    padTop: pad,
    padBottom: pad,
    gapsSpent: pad < gap,
    hardBand: bandAbove,
  };
}

/**
 * ── Round2 问题2：玩家详情「风格卡」的翻转 + 四边夹取放置（设计 §4.2.2 FR-B2）─────────
 *
 * 风格卡改由 portal 渲染到 overlay 根，脱离毛毡裁剪与公共牌带压制。定位规则按 pod（触发头像）的
 * **屏幕位置**决定展开方向，再对四边做视口 clamp，保证「完整落视口内（越界像素 = 0）」：
 *   - 左列（cx < 视口 35%）→ 卡在头像**右侧**展开（左沿贴头像右缘 + gap）
 *   - 右列（cx > 视口 65%）→ 卡在头像**左侧**展开（右沿贴头像左缘 − gap）
 *   - 顶弧（cy < 视口 40%，且非左右列）→ 卡在头像**下方**展开
 *   - 底部/英雄附近（其余）→ 卡在头像**上方**展开
 * 纯函数（输入全为实测几何），便于毫秒级单测锁定，不必跑浏览器。
 */
export interface StyleCardAnchor {
  /** 触发器（头像/昵称按钮）的视口坐标矩形。 */
  trigger: { left: number; top: number; right: number; bottom: number; width: number; height: number };
  /** 卡片盒尺寸（宽 = min(240, 视口宽 − 16)；高 = 自然内容高，超出走内部滚动）。 */
  card: { w: number; h: number };
  /** 视口尺寸。 */
  viewport: { w: number; h: number };
  /** 卡与头像的视觉锚定间距，默认 6px。 */
  gap?: number;
  /** 四边安全边距，默认 8px。 */
  margin?: number;
  /** 顶栏下沿安全线（top 的下界），默认 = margin。 */
  topSafe?: number;
  /** 底部浮层槽上沿安全线（bottom 的上界），默认 = 视口高 − margin。 */
  bottomSafe?: number;
}

export interface StyleCardPlacement {
  /** 卡片左上角视口坐标（position:fixed 直接消费）。 */
  left: number;
  top: number;
  /** 展开方向（诊断 / 箭头指向用）。 */
  placement: 'right' | 'left' | 'below' | 'above';
  /** 限高（= 可用纵向带；卡自然高超过它时内部滚动）。 */
  maxHeight: number;
}

export function placeStyleCard(a: StyleCardAnchor): StyleCardPlacement {
  const gap = a.gap ?? 6;
  const m = a.margin ?? 8;
  const topSafe = a.topSafe ?? m;
  const bottomSafe = a.bottomSafe ?? a.viewport.h - m;
  const vw = a.viewport.w;
  const cx = a.trigger.left + a.trigger.width / 2;
  const cy = a.trigger.top + a.trigger.height / 2;
  const cw = a.card.w;

  /* 可用纵向带 = [topSafe, bottomSafe]；卡自然高超过它就限高 + 内部滚动，绝不越界。 */
  const maxHeight = Math.max(0, bottomSafe - topSafe);
  const ch = Math.min(a.card.h, maxHeight);

  let placement: StyleCardPlacement['placement'];
  let left: number;
  let top: number;
  if (cx < vw * 0.35) {
    placement = 'right';
    left = a.trigger.right + gap;
    top = cy - ch / 2;
  } else if (cx > vw * 0.65) {
    placement = 'left';
    left = a.trigger.left - cw - gap;
    top = cy - ch / 2;
  } else if (cy < a.viewport.h * 0.4) {
    placement = 'below';
    left = cx - cw / 2;
    top = a.trigger.bottom + gap;
  } else {
    placement = 'above';
    left = cx - cw / 2;
    top = a.trigger.top - ch - gap;
  }

  /* 四边 clamp（越界像素 = 0）：left∈[m, vw−m−cw]、top∈[topSafe, bottomSafe−ch]。
     max(下界, …) 防在极窄视口下 cw/ch 超过可用宽高时算出反向区间。 */
  left = Math.min(Math.max(m, left), Math.max(m, vw - m - cw));
  top = Math.min(Math.max(topSafe, top), Math.max(topSafe, bottomSafe - ch));
  return { left: Math.round(left), top: Math.round(top), placement, maxHeight };
}
